import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import EmptyState from 'components/EmptyState';
import { FactCheckOutlined, PanelRightCloseIcon, PanelRightIcon, PlusIcon, XIcon } from 'components/icons';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { Switch } from 'components/ui/switch';
import { Tooltip } from 'components/ui/tooltip';
import useAuth from 'hooks/useAuth';
import { JustAddedHighlight } from 'pages/debriefing/components/JustAddedHighlight';
import {
    useCreateInvoiceCommentMutation,
    useDeleteInvoiceCommentMutation,
    useSetInvoiceCommentHandledMutation,
    useUpdateInvoiceCommentMutation,
} from 'store/api/invoiceComments';
import type { JSONContent } from '@tiptap/core';
import type { InvoiceComment } from 'types/invoiceComments';
import { cn } from 'utils/cn';
import { useHasPermissions } from 'utils/permissionUtil';
import { buildThreads, CommentNode, countOpen, liveReplies } from './commentTree';
import type { CommentsSession } from './commentsSession';
import { InvoiceCommentComposer } from './InvoiceCommentComposer';
import { InvoiceCommentEntry, MAX_INDENT } from './InvoiceCommentEntry';
import { useHideHandled } from './useHideHandled';
import { useInvoiceComments } from './useInvoiceComments';

const CREATE = ['CREATE.INVOICES.COMMENTS'];
const UPDATE_ANY = ['UPDATE.INVOICES.COMMENTS'];
const UPDATE_OWN = ['UPDATE.INVOICES.COMMENTS_MY_OWN'];
const HANDLE = ['UPDATE.INVOICES.COMMENTS_HANDLE'];
const DELETE_ANY = ['DELETE.INVOICES.COMMENTS'];
const DELETE_OWN = ['DELETE.INVOICES.COMMENTS_MY_OWN'];

// One step of the indent is the rule's 12px; each step after that adds the avatar and its gap.
const INDENT_CLASS = ['', 'ms-3', 'ms-[37px]', 'ms-[62px]'];

type Props = {
    invoiceId: number;
    onClose: () => void;
    /** The entry a notification opened the comments on: highlighted, shown even under Hide handled. */
    linkedCommentId?: number | null;
    /** What the user is doing in the comments, kept above both hosts. */
    session: CommentsSession;
    /** The docked column keeps its last entry clear of classic's floating buttons over the window's corner. */
    host: 'drawer' | 'column';
    /** The Dock or Undock button, where there is room for the column. */
    dock?: { docked: boolean; onToggle: () => void };
    /** Where the focus goes as the view is drawn: the heading, or the Dock or Undock button that replaced its twin. */
    focusOnShow?: 'heading' | 'dock' | null;
};

/** The comment log of one invoice, as the drawer and the docked column show it. */
export function InvoiceCommentsView({
    invoiceId,
    onClose,
    linkedCommentId = null,
    session,
    host,
    dock,
    focusOnShow = null,
}: Props) {
    const intl = useIntl();
    const { user } = useAuth();
    const { openModal, closeModal } = useModal();
    const { data, isLoading, isError, refetch } = useInvoiceComments(invoiceId);
    const [createComment] = useCreateInvoiceCommentMutation();
    const [updateComment] = useUpdateInvoiceCommentMutation();
    const [setHandled] = useSetInvoiceCommentHandledMutation();
    const [deleteComment] = useDeleteInvoiceCommentMutation();

    const canCreate = useHasPermissions(CREATE);
    const canEditAny = useHasPermissions(UPDATE_ANY);
    const canEditOwn = useHasPermissions(UPDATE_OWN);
    const canHandle = useHasPermissions(HANDLE);
    const canDeleteAny = useHasPermissions(DELETE_ANY);
    const canDeleteOwn = useHasPermissions(DELETE_OWN);

    const [hideHandled, rememberHideHandled] = useHideHandled(user?.id);
    // A linked entry shows under Hide handled until the switch is next turned.
    const { composer, openComposer: setComposer, revealed, setRevealed } = session;
    const list = useRef<HTMLDivElement | null>(null);
    const heading = useRef<HTMLHeadingElement | null>(null);
    const dockButton = useRef<HTMLButtonElement | null>(null);
    const addButton = useRef<HTMLButtonElement | null>(null);
    const comments = data?.comments;

    // The group the focus goes to once it is drawn: a saved entry, or a neighbour of a removed one. Kept in a ref
    // and taken by the effect that finds it drawn; `focusRequest` makes the next render look again.
    const focusTarget = useRef<number | 'add' | null>(null);
    const [focusRequest, setFocusRequest] = useState(0);
    const requestFocus = (target: number | 'add') => {
        focusTarget.current = target;
        setFocusRequest((request) => request + 1);
    };
    useEffect(() => {
        const target = focusTarget.current;
        if (target === null || !comments) return;
        const element =
            target === 'add'
                ? addButton.current
                : list.current?.querySelector<HTMLElement>(`[data-comment-group="${target}"]`);
        if (element) {
            element.scrollIntoView({ block: 'nearest' });
            element.focus();
            focusTarget.current = null;
        }
    }, [focusRequest, comments]);

    // An arrival from a notification brings its entry into view and takes the focus to it, once per link, not again
    // as the view moves to the other host.
    const { arrivedAt, scrollTop } = session;
    useEffect(() => {
        if (linkedCommentId === null) {
            arrivedAt.current = null;
            return;
        }
        const group = list.current?.querySelector<HTMLElement>(`[data-comment-group="${linkedCommentId}"]`);
        if (group && arrivedAt.current !== linkedCommentId) {
            group.scrollIntoView({ block: 'center' });
            group.focus();
            arrivedAt.current = linkedCommentId;
        }
    }, [linkedCommentId, comments, arrivedAt]);

    // The view drawn in the other host goes on where the list was read, and takes the focus it is handed.
    const restored = useRef(false);
    useLayoutEffect(() => {
        const element = list.current;
        if (restored.current || !element || !comments) return;
        restored.current = true;
        if (scrollTop.current?.invoiceId === invoiceId) element.scrollTop = scrollTop.current.top;
    }, [comments, invoiceId, scrollTop]);
    useEffect(() => {
        (focusOnShow === 'heading' ? heading : focusOnShow === 'dock' ? dockButton : null)?.current?.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- focused once, as the view is drawn
    }, []);

    const threads = comments ? buildThreads(comments) : [];
    const containsEntry = (node: CommentNode, id: number | null): boolean =>
        node.comment.id === id || node.replies.some((reply) => containsEntry(reply, id));
    const shownThreads = threads.filter(
        (thread) => !hideHandled || thread.comment.handledAt === null || containsEntry(thread, revealed)
    );
    const open = countOpen(comments);

    const nameOf = (comment: InvoiceComment) => comment.author?.name ?? intl.formatMessage({ id: 'unknown' });
    const isOwn = (comment: InvoiceComment) => comment.author !== null && comment.author.id === user?.id;
    const opener = () => (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const keepDraft = (content: JSONContent) => {
        session.draft.current = content;
    };
    const closeComposer = (focusEntryId?: number) => {
        if (focusEntryId !== undefined) {
            requestFocus(focusEntryId);
        } else if (composer?.opener?.isConnected) {
            composer.opener.focus();
        } else if (composer) {
            // The opener left with the composer (Edit, or the empty state's Add comment): its entry, or Add comment.
            requestFocus(
                composer.kind === 'edit' ? composer.comment.id : composer.kind === 'reply' ? composer.parent.id : 'add'
            );
        }
        setComposer(null);
    };

    /** The group the focus goes to as the groups `goes` picks leave: the next that stays, else the last before. */
    const neighbour = (id: number, goes: (groupId: number) => boolean): number | 'add' => {
        const ids = [...(list.current?.querySelectorAll<HTMLElement>('[data-comment-group]') ?? [])].map((group) =>
            Number(group.dataset.commentGroup)
        );
        const from = ids.indexOf(id);
        const stays = (groupId: number) => !goes(groupId);
        return ids.slice(from + 1).find(stays) ?? ids.slice(0, from).findLast(stays) ?? 'add';
    };

    const confirmDelete = (node: CommentNode) => {
        const isReply = node.comment.parentId !== null;
        openModal(
            <ConfirmationModal
                titleId="invoice-comments.delete-title"
                titleValues={{ kind: isReply ? 'reply' : 'comment' }}
                confirmId={
                    isReply ? 'invoice-comments.delete-confirm-reply' : 'invoice-comments.delete-confirm-comment'
                }
                content={
                    <p className="text-body1">
                        <FormattedMessage id="invoice-comments.delete-body" values={{ replies: liveReplies(node) }} />
                    </p>
                }
                onCancel={closeModal}
                onConfirm={() => {
                    const next = neighbour(node.comment.id, (groupId) => groupId === node.comment.id);
                    void deleteComment({ invoiceId, commentId: node.comment.id })
                        .unwrap()
                        .then(() => requestFocus(next))
                        .catch(console.error)
                        .finally(() => closeModal());
                }}
            />
        );
    };

    const toggleHandled = (thread: CommentNode) => {
        const marking = thread.comment.handledAt === null;
        const leaves = marking && hideHandled && !containsEntry(thread, revealed);
        const next = leaves ? neighbour(thread.comment.id, (groupId) => containsEntry(thread, groupId)) : null;
        void setHandled({ invoiceId, commentId: thread.comment.id, handled: marking })
            .unwrap()
            .then(() => next !== null && requestFocus(next))
            .catch(console.error);
    };

    const replyComposer = (parent: InvoiceComment, depth: number) => (
        <div
            key={`reply-${parent.id}`}
            className={cn('border-divider border-s ps-3 pt-3', INDENT_CLASS[Math.min(depth, MAX_INDENT)])}
        >
            <InvoiceCommentComposer
                invoiceId={invoiceId}
                label={intl.formatMessage({ id: 'invoice-comments.reply-label' }, { name: nameOf(parent) })}
                submitLabelId="invoice-comments.save-reply"
                initialContent={session.draft.current}
                onContentChange={keepDraft}
                onCancel={() => closeComposer()}
                onSubmit={(text, mentions) =>
                    createComment({ invoiceId, parentId: parent.id, text, mentions })
                        .unwrap()
                        .then((id) => closeComposer(id))
                }
            />
        </div>
    );

    const rows = (node: CommentNode, depth: number, thread: CommentNode, repliedTo?: InvoiceComment): ReactNode[] => {
        const { comment } = node;
        const editing = composer?.kind === 'edit' && composer.comment.id === comment.id;
        const own = isOwn(comment);
        const entry = (
            <InvoiceCommentEntry
                comment={comment}
                depth={depth}
                repliedToName={repliedTo && !repliedTo.deleted ? nameOf(repliedTo) : undefined}
                handledCard={thread.comment.handledAt !== null}
                actions={{
                    onReply: canCreate
                        ? () => setComposer({ kind: 'reply', parent: comment, opener: opener() })
                        : undefined,
                    onEdit:
                        canEditAny || (canEditOwn && own)
                            ? () => setComposer({ kind: 'edit', comment, opener: opener() })
                            : undefined,
                    onDelete: canDeleteAny || (canDeleteOwn && own) ? () => confirmDelete(node) : undefined,
                    onToggleHandled: canHandle && depth === 0 ? () => toggleHandled(thread) : undefined,
                }}
                editor={
                    editing ? (
                        <div className="mt-2">
                            <InvoiceCommentComposer
                                invoiceId={invoiceId}
                                label={intl.formatMessage(
                                    { id: 'invoice-comments.edit-label' },
                                    { name: nameOf(comment) }
                                )}
                                submitLabelId="save"
                                initialText={comment.text ?? ''}
                                initialMentions={comment.mentions}
                                initialContent={session.draft.current}
                                onContentChange={keepDraft}
                                onCancel={() => closeComposer()}
                                onSubmit={(text, mentions) =>
                                    updateComment({ invoiceId, commentId: comment.id, text, mentions })
                                        .unwrap()
                                        .then(() => closeComposer(comment.id))
                                }
                            />
                        </div>
                    ) : undefined
                }
            />
        );
        const indent = Math.min(depth, MAX_INDENT);
        const row = (
            <div
                key={comment.id}
                className={cn(depth > 0 && 'border-divider border-s ps-3 pt-3', INDENT_CLASS[indent])}
            >
                {comment.id === linkedCommentId ? (
                    <JustAddedHighlight key={linkedCommentId} className="-m-1.5 p-1.5">
                        {entry}
                    </JustAddedHighlight>
                ) : (
                    entry
                )}
            </div>
        );
        return [
            row,
            ...node.replies.flatMap((reply) => rows(reply, depth + 1, thread, comment)),
            ...(composer?.kind === 'reply' && composer.parent.id === comment.id
                ? [replyComposer(comment, depth + 1)]
                : []),
        ];
    };

    const addCommentButton = canCreate ? (
        <Button
            ref={addButton}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setComposer({ kind: 'new', opener: opener() })}
        >
            <PlusIcon data-icon="inline-start" />
            <FormattedMessage id="invoice-comments.add" />
        </Button>
    ) : null;

    const hasComments = (comments?.length ?? 0) > 0;

    let body: ReactNode;
    if (isLoading) {
        body = (
            <div className="flex flex-col gap-3" aria-busy="true">
                {[0, 1, 2].map((index) => (
                    <Skeleton key={index} className="new:rounded-xl h-24 rounded-lg" />
                ))}
            </div>
        );
    } else if (isError && !data) {
        body = <EmptyState size="panel" variant="error" onRetry={() => void refetch()} />;
    } else if (!hasComments && composer?.kind !== 'new') {
        body = (
            <EmptyState
                size="panel"
                icon={FactCheckOutlined}
                titleId="invoice-comments.empty-title"
                bodyId="invoice-comments.empty-body"
                action={addCommentButton}
            />
        );
    } else if (shownThreads.length === 0 && composer?.kind !== 'new') {
        body = <EmptyState size="panel" variant="filtered" onClearFilters={() => rememberHideHandled(false)} />;
    } else {
        body = (
            <div className="flex flex-col gap-3">
                {composer?.kind === 'new' ? (
                    <InvoiceCommentComposer
                        invoiceId={invoiceId}
                        label={intl.formatMessage({ id: 'invoice-comments.composer-label' })}
                        submitLabelId="invoice-comments.save-new"
                        initialContent={session.draft.current}
                        onContentChange={keepDraft}
                        onCancel={() => closeComposer()}
                        onSubmit={(text, mentions) =>
                            createComment({ invoiceId, parentId: null, text, mentions })
                                .unwrap()
                                .then((id) => closeComposer(id))
                        }
                    />
                ) : null}
                {shownThreads.map((thread) => (
                    <article
                        key={thread.comment.id}
                        aria-label={
                            thread.comment.deleted
                                ? intl.formatMessage({ id: 'invoice-comments.deleted-card-label' })
                                : intl.formatMessage(
                                      { id: 'invoice-comments.card-label' },
                                      { name: nameOf(thread.comment) }
                                  )
                        }
                        className={cn(
                            'border-divider new:rounded-xl new:border-0 rounded-lg border p-4',
                            thread.comment.handledAt !== null
                                ? 'new:bg-muted bg-grey-50'
                                : 'bg-paper new:shadow-card new:outline-hidden'
                        )}
                    >
                        {rows(thread, 0, thread)}
                    </article>
                ))}
            </div>
        );
    }

    const number = data?.invoiceNumber ?? '';
    const dockLabel = intl.formatMessage({ id: dock?.docked ? 'invoice-comments.undock' : 'invoice-comments.dock' });

    return (
        <div className="bg-paper text-fg flex size-full min-h-0 flex-col">
            <div className="flex items-start justify-between gap-2 px-6 pt-5 pb-3">
                <div className="min-w-0">
                    <h2
                        ref={heading}
                        tabIndex={-1}
                        className="text-foreground m-0 text-base font-semibold outline-none"
                    >
                        <FormattedMessage id="invoice-comments.title" />
                    </h2>
                    <p className="text-muted-foreground m-0 mt-0.5 text-xs">
                        <FormattedMessage id="invoice-comments.subtitle" values={{ open, number }} />
                    </p>
                </div>
                <div className="-me-2 flex shrink-0 items-center">
                    {dock ? (
                        <Tooltip content={dockLabel} side="left">
                            <Button
                                ref={dockButton}
                                // The drawer opened by Undock starts on this button, its twin's place.
                                data-comments-dock=""
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                aria-label={dockLabel}
                                onClick={dock.onToggle}
                            >
                                {dock.docked ? <PanelRightCloseIcon /> : <PanelRightIcon />}
                            </Button>
                        </Tooltip>
                    ) : null}
                    <Tooltip content={intl.formatMessage({ id: 'close' })} side="left">
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={intl.formatMessage({ id: 'close' })}
                            onClick={onClose}
                        >
                            <XIcon />
                        </Button>
                    </Tooltip>
                </div>
            </div>
            {hasComments ? (
                <div className="border-divider flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-6 pt-1 pb-4">
                    {addCommentButton}
                    <label className="text-fg ms-auto flex cursor-pointer items-center gap-2 text-sm">
                        <span>
                            <FormattedMessage id="invoice-comments.hide-handled" />
                        </span>
                        <Switch
                            checked={hideHandled}
                            onCheckedChange={(checked) => {
                                setRevealed(null);
                                rememberHideHandled(checked);
                            }}
                        />
                    </label>
                </div>
            ) : null}
            <div
                ref={list}
                onScroll={(event) => {
                    scrollTop.current = { invoiceId, top: event.currentTarget.scrollTop };
                }}
                className={cn(
                    'relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-4 pb-6',
                    host === 'column' && 'new:pb-6 pb-28'
                )}
            >
                {body}
            </div>
        </div>
    );
}
